import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Calculator, BookOpen, List, Cake, AlertTriangle, Package, Scale, CookingPot } from 'lucide-react';

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Cual es el food cost objetivo en una pasteleria?',
    a: 'El food cost objetivo en pasteleria es mas bajo que en cocina: entre el 20% y el 30%. Las tartas de autor y la bolleria artesana suelen estar en el 18-25%, mientras que los postres con ingredientes premium (chocolate Valrhona, vainilla de Madagascar, frutos rojos fuera de temporada) pueden llegar al 28-32%. Si tu food cost supera el 30% de forma consistente, necesitas revisar precios de venta o buscar proveedores alternativos.',
  },
  {
    q: '¿Por que el chocolate cobertura es tan caro para escandallar?',
    a: 'El chocolate cobertura de calidad (Valrhona, Callebaut, Cacao Barry) oscila entre 14 y 22 euros/kg dependiendo del porcentaje de cacao y la marca. Es el ingrediente que mas pesa en el coste de la mayoria de elaboraciones de pasteleria. La diferencia entre usar cobertura premium (18 euros/kg) y cobertura industrial (6-8 euros/kg) puede duplicar el food cost de una tarta, pero la diferencia de sabor es enorme. La clave es comunicar al cliente que usas chocolate premium para justificar el PVP.',
  },
  {
    q: '¿Como calculo la merma en pasteleria?',
    a: 'Las mermas en pasteleria son distintas a las de cocina. Las principales son: recortes de bizcocho al nivelar (5-10%), chocolate desperdiciado en el temperado (3-5%), nata que queda en el bol (2-3%), masa sobrante de recortes (5-8%), y decoraciones fallidas (variable). Para calcularlas, pesa el producto bruto antes de la elaboracion y el producto neto final. La formula es: Merma (%) = ((Peso bruto - Peso neto) / Peso bruto) x 100.',
  },
  {
    q: '¿Debo incluir el coste del packaging en el escandallo?',
    a: 'Si, absolutamente. En pasteleria, el packaging (cajas, bases de carton dorado, celofan, lazos, etiquetas, bolsas) puede representar entre el 5% y el 15% del coste total del producto. Una caja para tarta de 24 cm cuesta entre 0,80 y 2,50 euros, y una caja de bombones premium puede costar 3-5 euros. Si no incluyes el packaging en el escandallo, estas subestimando el coste real y tu margen es menor del que crees.',
  },
  {
    q: '¿Cuantas raciones salen de una tarta de 24 cm?',
    a: 'De una tarta redonda de 24 cm de diametro salen entre 10 y 14 raciones, dependiendo del tipo de tarta y del tamano de la porcion. Para tartas altas con varias capas (tipo layer cake): 12-14 raciones. Para tartas tipo tarta de queso o mousse (una sola capa): 10-12 raciones. Para calcular el coste por racion, divide el coste total de la tarta entre el numero de raciones. Es un error comun cortar raciones demasiado grandes, lo que reduce el numero de porciones y sube el food cost.',
  },
  {
    q: '¿Como afecta la estacionalidad al escandallo de pasteleria?',
    a: 'La estacionalidad impacta principalmente en frutas y frutos rojos. Las fresas en temporada (marzo-junio) cuestan 3-5 euros/kg, pero fuera de temporada pueden llegar a 12-15 euros/kg. Los frambuesas pasan de 8 euros/kg en verano a 18-22 euros/kg en invierno. Para mantener el food cost controlado, adapta tu oferta a la temporada: tartas de frutos rojos en verano, tartas de chocolate y frutos secos en invierno, tartas de citricos en otono-invierno.',
  },
  {
    q: '¿Es rentable vender bombones artesanales?',
    a: 'Los bombones artesanales son uno de los productos mas rentables de la pasteleria si se gestionan bien. El coste de materia prima por bombon oscila entre 0,25 y 0,60 euros, y el PVP por unidad suele estar entre 2,00 y 4,00 euros, lo que da un food cost del 12-20%. La clave de rentabilidad esta en la mano de obra (son intensivos en tiempo) y en las mermas por temperado fallido. Una caja de 12 bombones con un coste de materia prima de 4-6 euros se vende por 28-45 euros.',
  },
  {
    q: '¿Que software puedo usar para escandallar en pasteleria?',
    a: 'Puedes usar Excel (con las limitaciones de actualizacion manual de precios), hojas de calculo de Google, o un software especializado como PLEKO que calcula automaticamente el coste de tus recetas, actualiza precios al escanear albaranes con OCR, y te alerta cuando el food cost de una elaboracion se dispara. En pasteleria, donde los precios del chocolate, la mantequilla y los frutos secos fluctuan mucho, tener precios actualizados automaticamente es especialmente valioso.',
  },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Escandallo de Pasteleria: Calcula el Coste Exacto de Tartas, Postres y Bolleria',
  description: 'Guia completa de escandallos para pastelerias y obradores. 3 ejemplos reales con precios actualizados: tarta de chocolate, cheesecake y caja de bombones. Mermas, packaging y food cost objetivo.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: { '@type': 'Organization', name: 'PLEKO', logo: { '@type': 'ImageObject', url: 'https://pleko.app/favicon.svg' } },
  datePublished: '2026-06-18',
  dateModified: '2026-06-18',
  image: 'https://pleko.app/chef-hero.jpg',
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Escandallo', item: 'https://pleko.app/escandallo' },
    { '@type': 'ListItem', position: 3, name: 'Escandallo de Pasteleria', item: 'https://pleko.app/escandallo-pasteleria' },
  ],
};

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'por-que-especifico', label: 'Por que la pasteleria necesita escandallos especificos' },
  { id: 'ingredientes-precios', label: 'Ingredientes tipicos y sus precios' },
  { id: 'ejemplo-tarta-chocolate', label: 'Ejemplo: escandallo de tarta de chocolate' },
  { id: 'ejemplo-cheesecake', label: 'Ejemplo: escandallo de cheesecake' },
  { id: 'ejemplo-bombones', label: 'Ejemplo: escandallo de caja de bombones' },
  { id: 'mermas-pasteleria', label: 'Mermas en pasteleria' },
  { id: 'decoracion-packaging', label: 'Coste de decoracion y packaging' },
  { id: 'food-cost-objetivo', label: 'Food cost objetivo en pasteleria' },
  { id: 'errores-comunes', label: 'Errores comunes al escandallar en pasteleria' },
  { id: 'software-vs-excel', label: 'Software vs Excel para pastelerias' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Component ──────────────────────────────────────── */

export default function EscandalloPasteleriaPage() {
  return (
    <>
      <Helmet>
        <title>Escandallo de Pasteleria — Calcula el Coste de Tartas, Postres y Bolleria | PLEKO</title>
        <meta
          name="description"
          content="Guia completa de escandallos para pastelerias y obradores. 3 ejemplos reales con precios actualizados: tarta de chocolate, cheesecake y caja de bombones. Mermas, packaging y food cost objetivo."
        />
        <meta
          name="keywords"
          content="escandallo pasteleria, escandallo reposteria, costes pasteleria, food cost pasteleria, escandallo tarta, coste tarta chocolate, escandallo obrador, escandallo bolleria, escandallo bombones, food cost reposteria, escandallo postres, coste pasteleria artesanal"
        />
        <link rel="canonical" href="https://pleko.app/escandallo-pasteleria" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/escandallo-pasteleria" />
        <meta property="og:title" content="Escandallo de Pasteleria — Calcula el Coste de Tartas, Postres y Bolleria" />
        <meta property="og:description" content="Guia completa de escandallos para pastelerias. 3 ejemplos reales con precios actualizados, mermas especificas y food cost objetivo." />
        <meta property="og:image" content="https://pleko.app/chef-hero.jpg" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Escandallo de Pasteleria — Tartas, Postres y Bolleria | PLEKO" />
        <meta name="twitter:description" content="3 ejemplos reales de escandallos de pasteleria con precios actualizados, mermas y food cost objetivo." />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-pink-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-medium mb-6">
                <Cake className="w-4 h-4" />
                Guia especializada para pastelerias y obradores — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Escandallo de <span className="text-pink-500">Pasteleria</span>: Calcula el Coste Exacto de Tartas, Postres y Bolleria
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                La pasteleria trabaja con ingredientes caros, precisiones al gramo y mermas invisibles. Aprende a escandallar tartas, postres y bombones con ejemplos reales y precios de mercado actualizados.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#ejemplo-tarta-chocolate"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-pink-500 text-white font-semibold rounded-xl hover:bg-pink-600 transition-colors shadow-lg shadow-pink-200"
                >
                  Ver ejemplos de escandallos
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/calculadora-escandallo"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <Calculator className="w-4 h-4" />
                  Calculadora de costes
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-pink-500" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta guia</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-pink-600 hover:text-pink-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="prose prose-lg max-w-none">

            {/* Section 1: Por que la pasteleria necesita escandallos especificos */}
            <h2 id="por-que-especifico" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Por que la pasteleria necesita escandallos especificos</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El <strong>escandallo en pasteleria</strong> comparte la logica del <Link to="/escandallo" className="text-pink-600 hover:underline">escandallo clasico de cocina</Link>, pero tiene particularidades que lo hacen unico. Mientras que en cocina se trabaja con cantidades aproximadas ("un chorro de aceite", "sal al gusto"), en pasteleria <strong>la precision al gramo es obligatoria</strong>. Un error de 20 gramos en la harina o 10 ml en la nata puede arruinar una elaboracion entera.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Ademas, la pasteleria trabaja con <strong>ingredientes significativamente mas caros</strong> que la cocina. El chocolate cobertura premium (18-22 euros/kg), la vainilla en rama (600-800 euros/kg), los frutos secos (15-35 euros/kg) y la mantequilla de calidad (9-12 euros/kg) hacen que cualquier error de calculo tenga un impacto economico mucho mayor que en un plato de cocina.
            </p>

            <div className="bg-pink-50 border border-pink-100 rounded-xl p-6 mb-8">
              <p className="text-pink-800 font-medium text-center text-lg">
                Diferencias clave: escandallo de pasteleria vs cocina
              </p>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-pink-600">20-28%</p>
                  <p className="text-sm text-slate-600 mt-1">Food cost objetivo pasteleria</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-500">28-35%</p>
                  <p className="text-sm text-slate-600 mt-1">Food cost objetivo cocina</p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-slate-800 mb-4">5 razones por las que el escandallo de pasteleria es distinto</h3>
            <ul className="space-y-3 mb-8">
              {[
                'Precision al gramo — un error de pesaje puede arruinar una masa o una ganache entera',
                'Ingredientes caros — chocolate, vainilla, frutos secos y mantequilla premium elevan el coste base',
                'Mermas invisibles — recortes de bizcocho, chocolate desperdiciado en temperado, nata residual en boles',
                'Packaging obligatorio — cajas, bases, celofan y lazos son un coste real que muchos olvidan incluir',
                'Produccion por lotes — se elaboran 10-20 unidades, lo que exige escandallo por pieza y por lote completo',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-pink-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            {/* Section 2: Ingredientes tipicos y sus precios */}
            <h2 id="ingredientes-precios" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Ingredientes Tipicos de Pasteleria y sus Precios</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Antes de construir un escandallo, necesitas conocer los <strong>precios actualizados de los ingredientes clave</strong>. Estos son los precios medios de distribuidor en Espana (junio 2026) para los ingredientes mas habituales en pasteleria profesional:
            </p>

            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-pink-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Precio medio</th>
                    <th className="p-3 text-left font-medium">Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Chocolate cobertura 70% (Valrhona Guanaja)', '18,50 euros/kg', 'Premium. Industrial: 6-8 euros/kg'],
                    ['Chocolate con leche cobertura (Callebaut)', '14,20 euros/kg', 'Gama media-alta'],
                    ['Chocolate blanco cobertura', '13,80 euros/kg', 'Mayor contenido de manteca de cacao'],
                    ['Mantequilla francesa (82% MG)', '9,80 euros/kg', 'Esencial para hojaldre y croissants'],
                    ['Nata para montar 35% MG', '2,80 euros/L', 'Para ganaches, mousses y rellenos'],
                    ['Huevos camperos', '0,35 euros/ud', 'Clase 1. Camperos: +40% vs jaula'],
                    ['Harina de trigo floja', '0,95 euros/kg', 'Para bizcochos y masas blandas'],
                    ['Harina de fuerza', '1,20 euros/kg', 'Para bolleria y masas fermentadas'],
                    ['Azucar blanco', '1,10 euros/kg', 'Azucar glass: 2,50 euros/kg'],
                    ['Vainilla en rama (Madagascar)', '680,00 euros/kg', '~4,50 euros por vaina (6-7 g)'],
                    ['Extracto de vainilla puro', '85,00 euros/L', 'Alternativa mas economica a la rama'],
                    ['Almendra en polvo (marcona)', '22,00 euros/kg', 'Para macarons, financiers, frangipane'],
                    ['Avellana tostada en grano', '18,50 euros/kg', 'Para praline y decoracion'],
                    ['Pistacho pelado', '35,00 euros/kg', 'El fruto seco mas caro en pasteleria'],
                    ['Fresas (temporada)', '4,50 euros/kg', 'Fuera temporada: 10-14 euros/kg'],
                    ['Frambuesas', '12,00 euros/kg', 'Congeladas: 6-8 euros/kg'],
                    ['Gelatina en hojas', '28,00 euros/kg', '~0,14 euros por hoja (5 g)'],
                    ['Cacao puro en polvo', '16,00 euros/kg', 'Sin azucar, para espolvorear y masas'],
                    ['Levadura fresca', '4,50 euros/kg', '~0,10 euros por cubo de 25 g'],
                    ['Glucosa liquida', '3,80 euros/kg', 'Para dar brillo y textura a glaseados'],
                  ].map(([ingrediente, precio, obs], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ingrediente}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-pink-700">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500 text-sm">{obs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Observa como el <strong>chocolate, los frutos secos y la vainilla</strong> son los ingredientes que mas pesan en el coste. Una tarta que use 400 g de chocolate Valrhona (7,40 euros) y 100 g de almendra marcona (2,20 euros) ya tiene casi 10 euros de coste solo en dos ingredientes. Conocer estos precios es el primer paso para construir escandallos precisos. Si necesitas actualizar precios automaticamente, un <Link to="/software-escandallo" className="text-pink-600 hover:underline">software de escandallos</Link> lo hace al escanear cada albaran.
            </p>

            {/* Section 3: Ejemplo tarta de chocolate */}
            <h2 id="ejemplo-tarta-chocolate" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><Cake className="w-7 h-7 text-pink-500" /> Ejemplo Completo: Escandallo de Tarta de Chocolate Fondant</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Tarta clasica de obrador artesano. Bizcocho de chocolate con ganache de cobertura negra 70%. Molde de 24 cm, 12 raciones. Chocolate Valrhona Guanaja, mantequilla de Normandia, huevos camperos. Producto estrella de vitrina y encargos.
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-pink-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Unidad</th>
                    <th className="p-3 text-right font-medium">Precio/unidad</th>
                    <th className="p-3 text-right font-medium">Merma %</th>
                    <th className="p-3 text-right font-medium">Coste neto</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Chocolate negro 70% Valrhona', '400 g', 'kg', '18,50 euros/kg', '3%', '7,63 euros'],
                    ['Mantequilla 82% MG', '200 g', 'kg', '9,80 euros/kg', '0%', '1,96 euros'],
                    ['Huevos camperos', '6 ud', 'ud', '0,35 euros/ud', '10%', '2,31 euros'],
                    ['Azucar blanco', '180 g', 'kg', '1,10 euros/kg', '0%', '0,20 euros'],
                    ['Harina de trigo floja', '80 g', 'kg', '0,95 euros/kg', '0%', '0,08 euros'],
                    ['Nata 35% MG (ganache)', '250 ml', 'L', '2,80 euros/L', '2%', '0,71 euros'],
                    ['Cacao puro en polvo', '25 g', 'kg', '16,00 euros/kg', '0%', '0,40 euros'],
                    ['Extracto de vainilla', '5 ml', 'L', '85,00 euros/L', '0%', '0,43 euros'],
                    ['Sal Maldon', '2 g', 'kg', '38,00 euros/kg', '0%', '0,08 euros'],
                  ].map(([ing, qty, unidad, precio, merma, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500 text-sm">{unidad}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-pink-50">
                    <td colSpan={5} className="p-3 font-bold text-slate-800">COSTE TOTAL (12 raciones)</td>
                    <td className="p-3 text-right font-bold text-pink-600 text-lg">13,80 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div className="bg-pink-50 border border-pink-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total</p>
                <p className="text-2xl font-bold text-pink-600">13,80 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste / racion</p>
                <p className="text-2xl font-bold text-slate-800">1,15 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP racion</p>
                <p className="text-2xl font-bold text-slate-800">5,50 euros</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-green-600">20,9%</p>
                <p className="text-xs text-green-600 font-medium">Excelente</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-10">
              <p className="text-amber-800 font-semibold mb-1">Consejo profesional</p>
              <p className="text-slate-700 text-sm">
                El chocolate representa el <strong>55% del coste total</strong> de esta tarta. Si sustituyes Valrhona (18,50 euros/kg) por una cobertura de gama media como Callebaut (14,20 euros/kg), el coste total baja a 11,68 euros y el food cost por racion pasa al 17,7%. La pregunta es si tu cliente nota la diferencia. En encargos premium, usa Valrhona y comunicalo. En vitrina diaria, una buena cobertura de gama media puede ser suficiente.
              </p>
            </div>

            {/* Section 4: Ejemplo cheesecake */}
            <h2 id="ejemplo-cheesecake" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><CookingPot className="w-7 h-7 text-amber-500" /> Ejemplo: Escandallo de Cheesecake New York Style</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Cheesecake clasico estilo New York con base de galleta y queso crema Philadelphia. Molde de 22 cm, 10 raciones. Receta sencilla pero con ingredientes de alto coste unitario (queso crema, nata). Producto de alta rotacion en vitrina.
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-amber-700 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Unidad</th>
                    <th className="p-3 text-right font-medium">Precio/unidad</th>
                    <th className="p-3 text-right font-medium">Merma %</th>
                    <th className="p-3 text-right font-medium">Coste neto</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Queso crema Philadelphia', '600 g', 'kg', '7,90 euros/kg', '0%', '4,74 euros'],
                    ['Nata 35% MG', '200 ml', 'L', '2,80 euros/L', '2%', '0,57 euros'],
                    ['Huevos camperos', '3 ud', 'ud', '0,35 euros/ud', '10%', '1,16 euros'],
                    ['Azucar blanco', '150 g', 'kg', '1,10 euros/kg', '0%', '0,17 euros'],
                    ['Extracto de vainilla', '5 ml', 'L', '85,00 euros/L', '0%', '0,43 euros'],
                    ['Harina de trigo (ligante)', '20 g', 'kg', '0,95 euros/kg', '0%', '0,02 euros'],
                    ['Galleta digestive (base)', '200 g', 'kg', '4,80 euros/kg', '0%', '0,96 euros'],
                    ['Mantequilla (base)', '80 g', 'kg', '9,80 euros/kg', '0%', '0,78 euros'],
                    ['Zumo de limon', '15 ml', 'L', '3,20 euros/L', '0%', '0,05 euros'],
                  ].map(([ing, qty, unidad, precio, merma, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500 text-sm">{unidad}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-amber-50">
                    <td colSpan={5} className="p-3 font-bold text-slate-800">COSTE TOTAL (10 raciones)</td>
                    <td className="p-3 text-right font-bold text-amber-700 text-lg">8,88 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total</p>
                <p className="text-2xl font-bold text-amber-700">8,88 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste / racion</p>
                <p className="text-2xl font-bold text-slate-800">0,89 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP racion</p>
                <p className="text-2xl font-bold text-slate-800">4,90 euros</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-green-600">18,2%</p>
                <p className="text-xs text-green-600 font-medium">Excelente</p>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              El cheesecake es uno de los <strong>postres mas rentables</strong> de cualquier pasteleria. Con un food cost del 18,2%, cada racion genera <strong>4,01 euros de margen bruto</strong>. El queso crema es el ingrediente mas caro (53% del coste total), pero el PVP percibido por el cliente es alto porque el cheesecake se asocia con producto premium. Si vendes 8 raciones al dia, son 32 euros de margen solo con este producto. La clave: no escatimar en queso crema — usar uno mas barato se nota inmediatamente en la textura.
            </p>

            {/* Section 5: Ejemplo bombones */}
            <h2 id="ejemplo-bombones" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><Package className="w-7 h-7 text-purple-500" /> Ejemplo: Escandallo de Caja de 12 Bombones Artesanales</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-4">
              Caja surtida de 12 bombones artesanales con 4 variedades: ganache de chocolate negro, praline de avellana, trufa de cafe y bombon de fruta de la pasion. Incluye packaging premium (caja rigida con separadores). Producto de alta rotacion en Navidad, San Valentin y Dia de la Madre.
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-purple-800 text-white">
                    <th className="p-3 text-left font-medium">Ingrediente</th>
                    <th className="p-3 text-right font-medium">Cantidad</th>
                    <th className="p-3 text-left font-medium">Unidad</th>
                    <th className="p-3 text-right font-medium">Precio/unidad</th>
                    <th className="p-3 text-right font-medium">Merma %</th>
                    <th className="p-3 text-right font-medium">Coste neto</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Chocolate cobertura negro 64% (carcasas)', '180 g', 'kg', '16,50 euros/kg', '5%', '3,12 euros'],
                    ['Chocolate cobertura negro 70% (ganache)', '80 g', 'kg', '18,50 euros/kg', '3%', '1,53 euros'],
                    ['Chocolate con leche (praline)', '60 g', 'kg', '14,20 euros/kg', '3%', '0,88 euros'],
                    ['Nata 35% MG (ganaches)', '120 ml', 'L', '2,80 euros/L', '2%', '0,34 euros'],
                    ['Mantequilla (ganaches)', '30 g', 'kg', '9,80 euros/kg', '0%', '0,29 euros'],
                    ['Avellana tostada (praline)', '40 g', 'kg', '18,50 euros/kg', '0%', '0,74 euros'],
                    ['Cafe espresso (trufa)', '20 ml', 'L', '14,00 euros/kg en grano', '0%', '0,12 euros'],
                    ['Puree fruta de la pasion', '30 g', 'kg', '12,00 euros/kg', '0%', '0,36 euros'],
                    ['Glucosa liquida', '15 g', 'kg', '3,80 euros/kg', '0%', '0,06 euros'],
                    ['Manteca de cacao (brillo)', '10 g', 'kg', '22,00 euros/kg', '0%', '0,22 euros'],
                    ['Colorante alimentario liposoluble', '2 g', 'kg', '95,00 euros/kg', '0%', '0,19 euros'],
                    ['Caja rigida 12 bombones + separador', '1 ud', 'ud', '3,20 euros/ud', '0%', '3,20 euros'],
                  ].map(([ing, qty, unidad, precio, merma, coste], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{ing}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500 text-sm">{unidad}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{precio}</td>
                      <td className="p-3 border-b border-slate-100 text-right text-slate-600">{merma}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-medium text-slate-800">{coste}</td>
                    </tr>
                  ))}
                  <tr className="bg-purple-50">
                    <td colSpan={5} className="p-3 font-bold text-slate-800">COSTE TOTAL (caja de 12 bombones)</td>
                    <td className="p-3 text-right font-bold text-purple-600 text-lg">11,05 euros</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste total caja</p>
                <p className="text-2xl font-bold text-purple-600">11,05 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Coste / bombon</p>
                <p className="text-2xl font-bold text-slate-800">0,92 euros</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">PVP caja</p>
                <p className="text-2xl font-bold text-slate-800">38,00 euros</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-1">Food cost</p>
                <p className="text-2xl font-bold text-green-600">29,1%</p>
                <p className="text-xs text-green-600 font-medium">Bien</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-10">
              <p className="text-amber-800 font-semibold mb-1">Consejo profesional</p>
              <p className="text-slate-700 text-sm">
                El packaging representa el <strong>29% del coste total</strong> de esta caja de bombones (3,20 euros de 11,05 euros). Es un porcentaje enorme. Para reducirlo sin perder calidad percibida, negocia con proveedores de packaging por volumen: comprar 500 cajas en vez de 50 puede bajar el coste un 30-40%. Otra opcion: disenar cajas propias que refuercen tu marca y justifiquen el precio premium. El bombon suelto a 2,50-3,00 euros/ud no necesita caja y tiene un food cost del 15-20%.
              </p>
            </div>

            {/* Section 6: Mermas en pasteleria */}
            <h2 id="mermas-pasteleria" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Mermas Especificas de Pasteleria</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Las mermas en pasteleria son diferentes a las de cocina. No hay limpieza de vegetales ni deshuese de carne, pero hay perdidas especificas del oficio que debes conocer y cuantificar. Estas son las <strong>mermas tipicas</strong> en un obrador profesional. Si quieres profundizar en mermas de cocina, consulta nuestra <Link to="/mermas-cocina" className="text-pink-600 hover:underline">guia completa de mermas</Link>.
            </p>

            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de merma</th>
                    <th className="p-3 text-center font-medium">% tipico</th>
                    <th className="p-3 text-left font-medium">Causa y como minimizarla</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Recortes de bizcocho (nivelado)', '5-10%', 'Al nivelar bizcochos con cuchillo de sierra. Reutiliza los recortes para cake pops, trufas o base de postres en vaso.'],
                    ['Chocolate en temperado', '3-5%', 'Chocolate que cristaliza en la mesa, en las espatulas y en los moldes. Usa tecnica de siembra para minimizar perdidas.'],
                    ['Nata residual en bol', '2-3%', 'Nata montada o ganache que queda en las paredes del bol y la varilla. Usa espatulas de silicona para apurar.'],
                    ['Masa sobrante (recortes)', '5-8%', 'Recortes de hojaldre, masa quebrada, masa de galleta. Reincorpora los recortes hasta 2 veces sin perder calidad.'],
                    ['Huevos (cascara + clara/yema sobrante)', '10-12%', 'La cascara pesa un 10-12% del huevo total. Si la receta solo usa yemas, la clara es merma (o reutilizala en merengues, macarons).'],
                    ['Decoracion fallida', '2-5%', 'Flores de chocolate rotas, templeado incorrecto, glaseados con burbujas. La practica reduce esta merma con el tiempo.'],
                    ['Producto caducado / no vendido', '3-8%', 'Tartas y bolleria que no se venden antes de la fecha limite. Ajusta la produccion a la demanda real por dia de la semana.'],
                    ['Fruta fresca (pelado, deshuesado)', '15-35%', 'Fresas (5% tallo), melocotones (20% hueso+piel), mangos (30% hueso+piel). Usa fruta congelada para rellenos si el aspecto no importa.'],
                  ].map(([tipo, porcentaje, causa], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-amber-600">{porcentaje}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600 text-sm">{causa}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Section 7: Decoracion y packaging */}
            <h2 id="decoracion-packaging" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Como Incluir el Coste de Decoracion y Packaging</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Uno de los errores mas comunes en pasteleria es <strong>no incluir la decoracion y el packaging en el escandallo</strong>. Estos costes pueden representar entre el 8% y el 20% del coste total de una elaboracion. Aqui tienes una guia de precios orientativos:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="border border-pink-200 rounded-xl p-5">
                <h3 className="text-lg font-bold text-pink-700 mb-3 flex items-center gap-2">
                  <Cake className="w-5 h-5" /> Decoracion
                </h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex justify-between"><span>Flores comestibles (bolsa 20 uds)</span><span className="font-medium">4,50-8,00 euros</span></li>
                  <li className="flex justify-between"><span>Laminas de oro comestible (5 uds)</span><span className="font-medium">6,00-12,00 euros</span></li>
                  <li className="flex justify-between"><span>Sprinkles / virutas chocolate (100 g)</span><span className="font-medium">2,50-5,00 euros</span></li>
                  <li className="flex justify-between"><span>Frutas frescas decoracion (por tarta)</span><span className="font-medium">1,50-4,00 euros</span></li>
                  <li className="flex justify-between"><span>Glaseado espejo (por tarta)</span><span className="font-medium">1,20-2,50 euros</span></li>
                  <li className="flex justify-between"><span>Transfer de chocolate impreso</span><span className="font-medium">0,30-0,80 euros/ud</span></li>
                </ul>
              </div>
              <div className="border border-pink-200 rounded-xl p-5">
                <h3 className="text-lg font-bold text-pink-700 mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5" /> Packaging
                </h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex justify-between"><span>Caja tarta 24 cm (carton)</span><span className="font-medium">0,80-1,50 euros</span></li>
                  <li className="flex justify-between"><span>Caja tarta 24 cm (rigida premium)</span><span className="font-medium">2,00-3,50 euros</span></li>
                  <li className="flex justify-between"><span>Base dorada redonda 24 cm</span><span className="font-medium">0,40-0,80 euros</span></li>
                  <li className="flex justify-between"><span>Caja bombones 12 uds (rigida)</span><span className="font-medium">2,50-4,00 euros</span></li>
                  <li className="flex justify-between"><span>Bolsa de papel con logo</span><span className="font-medium">0,15-0,40 euros</span></li>
                  <li className="flex justify-between"><span>Etiqueta adhesiva personalizada</span><span className="font-medium">0,05-0,15 euros</span></li>
                </ul>
              </div>
            </div>

            <div className="bg-pink-50 border border-pink-200 rounded-xl p-4 mb-10">
              <p className="text-pink-800 font-semibold mb-1">Regla practica</p>
              <p className="text-slate-700 text-sm">
                Suma <strong>siempre</strong> el coste de decoracion + packaging al coste de ingredientes antes de calcular el food cost. Una tarta con coste de ingredientes de 13,80 euros, decoracion de 2,50 euros y caja + base de 2,30 euros tiene un coste real de <strong>18,60 euros</strong>, no de 13,80 euros. Si vendes esa tarta entera a 48 euros, tu food cost real es 38,8% (no el 28,8% que calcularias sin decoracion ni packaging). Esa diferencia de 10 puntos porcentuales puede ser la diferencia entre ganar y perder dinero.
              </p>
            </div>

            {/* Section 8: Food cost objetivo */}
            <h2 id="food-cost-objetivo" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><Scale className="w-7 h-7 text-pink-500" /> Food Cost Objetivo en Pasteleria</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El food cost en pasteleria suele ser <strong>mas bajo que en cocina</strong> porque el valor anadido de la mano de obra (decoracion, precision, tecnica) es mayor. Mientras que un restaurante apunta a un food cost del 28-32%, una pasteleria rentable se situa en el <strong>20-28%</strong>. Aqui tienes los rangos por tipo de producto:
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-pink-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo de producto</th>
                    <th className="p-3 text-center font-medium">Food Cost Objetivo</th>
                    <th className="p-3 text-center font-medium">Margen habitual</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Bolleria basica (croissant, napolitana)', '18-22%', 'Muy alto', 'bg-green-50'],
                    ['Tartas de vitrina (estandar)', '20-25%', 'Alto', 'bg-green-50'],
                    ['Tartas de encargo (personalizadas)', '22-28%', 'Alto', 'bg-green-50'],
                    ['Bombones y trufas', '15-22%', 'Muy alto', 'bg-green-50'],
                    ['Macarons', '18-25%', 'Alto', 'bg-green-50'],
                    ['Postres individuales (mousse, opera)', '20-26%', 'Alto', 'bg-green-50'],
                    ['Tartas con fruta fresca premium', '25-32%', 'Medio', 'bg-yellow-50'],
                    ['Panettone / productos estacionales', '22-28%', 'Alto', 'bg-green-50'],
                    ['Galletas y petit fours', '15-20%', 'Muy alto', 'bg-green-50'],
                    ['Tartas nupciales', '25-30%', 'Medio-alto', 'bg-yellow-50'],
                  ].map(([tipo, foodCost, margen, color], i) => (
                    <tr key={i} className={color}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{tipo}</td>
                      <td className="p-3 border-b border-slate-100 text-center font-semibold text-pink-700">{foodCost}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600">{margen}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              Los <strong>bombones, galletas y bolleria basica</strong> son los productos con mejor margen relativo. Los macarons, pese a ser productos de alto valor percibido, tienen un food cost controlado (18-25%) porque la almendra en polvo es el unico ingrediente caro. Las <strong>tartas con fruta fresca premium</strong> son las mas delicadas: las frambuesas fuera de temporada (18-22 euros/kg) pueden disparar el food cost por encima del 30%. Para mas informacion sobre food cost en hosteleria, consulta nuestra <Link to="/food-cost" className="text-pink-600 hover:underline">guia completa de food cost</Link>.
            </p>

            {/* Section 9: Errores comunes */}
            <h2 id="errores-comunes" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-2"><AlertTriangle className="w-7 h-7 text-red-500" /> 7 Errores Comunes al Escandallar en Pasteleria</span>
            </h2>

            <div className="space-y-6 mb-10">
              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">1. No incluir el packaging en el coste</h3>
                <p className="text-slate-700 leading-relaxed">
                  Como hemos visto en el ejemplo de bombones, el packaging puede representar hasta el 29% del coste total. Si vendes una tarta a 45 euros y no has incluido los 2,30 euros de caja + base, tu margen real es un 5% menor del que crees. Multiplica eso por 20 tartas a la semana y son 184 euros al mes de margen que no existe.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">2. Ignorar las mermas del temperado de chocolate</h3>
                <p className="text-slate-700 leading-relaxed">
                  El temperado de chocolate genera una merma del 3-5% por cristalizacion en la mesa, espatulas y moldes. Con chocolate a 18 euros/kg, una merma del 5% sobre 1 kg de chocolate son 0,93 euros de coste invisible. Si temperas chocolate a diario, esos centimos se convierten en cientos de euros al mes.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">3. No diferenciar entre tarta entera y racion</h3>
                <p className="text-slate-700 leading-relaxed">
                  Una tarta de chocolate con coste de 13,80 euros vendida a 48 euros tiene un food cost del 28,8%. Pero la misma tarta racionada en 12 porciones a 5,50 euros (66 euros en total) tiene un food cost del 20,9%. Siempre calcula ambos escenarios: venta entera y venta por racion, porque el PVP optimo puede variar.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">4. Usar el mismo precio de chocolate todo el ano</h3>
                <p className="text-slate-700 leading-relaxed">
                  El precio del cacao ha subido un 40% en los ultimos 2 anos. Si tu escandallo usa precios de hace 6 meses, estas subestimando el coste real. Actualiza los precios con cada factura o usa un <Link to="/software-escandallo" className="text-pink-600 hover:underline">software que lo haga automaticamente</Link>.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">5. No contabilizar claras o yemas sobrantes</h3>
                <p className="text-slate-700 leading-relaxed">
                  Muchas recetas usan solo yemas (crema pastelera, crema inglesa) o solo claras (merengue, macarons). Si no reutilizas la otra parte, la merma del huevo puede llegar al 50%. Planifica la produccion para usar claras y yemas complementariamente: hoy crema pastelera (yemas), manana macarons (claras).
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">6. Subestimar la merma por producto no vendido</h3>
                <p className="text-slate-700 leading-relaxed">
                  En pasteleria, la vida util del producto es corta: una tarta de fruta fresca dura 2-3 dias, la bolleria es de consumo en el dia. Si produces 10 tartas y vendes 7, las 3 restantes son merma pura. La solucion: analiza historicos de venta por dia de la semana y ajusta la produccion. Los lunes y martes se vende menos que jueves y viernes.
                </p>
              </div>

              <div className="border-l-4 border-red-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">7. No escalar el escandallo al cambiar el tamano del molde</h3>
                <p className="text-slate-700 leading-relaxed">
                  Una tarta en molde de 24 cm no usa el doble de ingredientes que una de 18 cm. La relacion es cuadratica (proporcional al area del circulo). Un molde de 24 cm tiene un area un 78% mayor que uno de 18 cm. Si simplemente duplicas ingredientes, estas sobrecosteando. Usa un escalador de recetas o calcula la proporcion: Area = pi * r2.
                </p>
              </div>
            </div>

            {/* Section 10: Software vs Excel */}
            <h2 id="software-vs-excel" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">Software vs Excel para Escandallar en Pasteleria</h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La mayoria de pastelerias empiezan con Excel (o con una <Link to="/plantilla-escandallo-excel" className="text-pink-600 hover:underline">plantilla de escandallo</Link>) y esta bien para empezar. Pero conforme crece el negocio, Excel muestra sus limitaciones. Aqui tienes una comparacion honesta:
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Aspecto</th>
                    <th className="p-3 text-center font-medium">Excel / Hoja de calculo</th>
                    <th className="p-3 text-center font-medium">Software (PLEKO)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Actualizacion de precios', 'Manual — cada vez que llega una factura', 'Automatica con OCR (foto al albaran)'],
                    ['Escalado de recetas', 'Manual — recalcular cada ingrediente', 'Automatico (escala a cualquier num. raciones)'],
                    ['Recetas vinculadas', 'No — cada receta es independiente', 'Si — el cambio de precio se propaga a todas'],
                    ['Coste en cascada (subrecetas)', 'Muy complejo de mantener', 'Automatico (ganache como subreceta de tarta)'],
                    ['Alertas de food cost', 'No', 'Si — avisa cuando un producto supera el objetivo'],
                    ['Gestion de mermas', 'Manual', 'Configurable por ingrediente, se aplica automaticamente'],
                    ['Acceso equipo / obrador', 'Dificil (archivos compartidos)', 'Multiusuario con roles (pastelero, jefe, admin)'],
                    ['Coste', 'Gratis', 'Desde 49 euros/mes'],
                    ['Ideal para', 'Pastelerias con <30 recetas', 'Obradores con produccion diaria variada'],
                  ].map(([aspecto, excel, software], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{aspecto}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-slate-600 text-sm">{excel}</td>
                      <td className="p-3 border-b border-slate-100 text-center text-pink-700 text-sm font-medium">{software}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              La ventaja clave del software es la <strong>actualizacion automatica de precios</strong>. Cuando el precio del chocolate sube un 10% y tienes 40 recetas que usan chocolate, en Excel tienes que actualizar 40 celdas manualmente. Con PLEKO, actualizas el precio del chocolate una vez (o escaneas el albaran con el movil) y las 40 recetas se recalculan al instante. En una pasteleria donde los precios del chocolate, la mantequilla y los frutos secos fluctuan constantemente, esta automatizacion ahorra horas de trabajo y evita errores de calculo.
            </p>
          </div>

          {/* FAQ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Preguntas frecuentes sobre escandallos de pasteleria</h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* Internal links */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><BookOpen className="w-5 h-5 text-pink-500" /> Guias relacionadas</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/escandallo', label: 'Escandallo en hosteleria: guia completa' },
                { to: '/ejemplo-escandallo', label: '8 ejemplos reales de escandallos' },
                { to: '/como-hacer-escandallo', label: 'Como hacer un escandallo paso a paso' },
                { to: '/escandallo-bebidas', label: 'Escandallo de bebidas y cocteles' },
                { to: '/escandallo-panaderia', label: 'Escandallo de panaderia' },
                { to: '/calculadora-escandallo', label: 'Calculadora de escandallo online gratis' },
                { to: '/software-escandallo', label: 'Software de escandallo para restaurantes' },
                { to: '/plantilla-escandallo-excel', label: 'Plantilla de escandallo en Excel (gratis)' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-pink-600 hover:text-pink-800 hover:underline">
                  <ArrowRight className="w-3 h-3" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl p-8 text-center text-white">
            <Cake className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">Calcula los escandallos de tu pasteleria automaticamente</h2>
            <p className="text-pink-100 mb-6 max-w-xl mx-auto">
              PLEKO calcula el coste exacto de todas tus elaboraciones, actualiza precios automaticamente al escanear albaranes con IA, controla mermas y te avisa cuando el food cost se dispara. Ideal para pastelerias y obradores.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-pink-600 font-bold rounded-xl hover:bg-pink-50 transition-colors"
            >
              Empezar gratis — 14 dias sin tarjeta
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-pink-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>
      </div>
    </>
  );
}
